// Updated to work with direct server API
const WALLETS_API_BASE_URL = 'https://pay-with-usdt.com/api';

class WalletsAPI {
  // Helper to find customer by email
  static async findCustomerByEmail(email) {
    try {

      // Try the direct email endpoint
      const url = `${WALLETS_API_BASE_URL}/customers/email/${encodeURIComponent(email)}`;

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json'
        }
      });


      if (response.ok) {
        const customer = await response.json();

        // Ensure we have walletIndex for compatibility
        // API returns wallet_index (snake_case), we need walletIndex (camelCase)
        if (customer) {
          if (customer.wallet_index !== undefined && customer.walletIndex === undefined) {
            customer.walletIndex = customer.wallet_index;
          }
          if (customer.index !== undefined && customer.walletIndex === undefined) {
            customer.walletIndex = customer.index;
          }
        }

        return customer;
      } else if (response.status === 404) {
        return null;
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      return null;
    }
  }

  // Register user using admin endpoint (secure)
  static async registerUserAdmin(email, displayName = null) {
    try {
      // Admin registration
      
      const response = await fetch(`${WALLETS_API_BASE_URL}/admin/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          name: displayName || email,
          email: email 
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        // Registration failed
        throw new Error(`Admin registration failed: ${response.status}`);
      }

      const result = await response.json();
      // Registration successful
      const customer = result.customer;

      // Ensure we have walletIndex for compatibility
      if (customer && customer.wallet_index !== undefined && customer.walletIndex === undefined) {
        customer.walletIndex = customer.wallet_index;
      }

      return customer;
    } catch (error) {
      // Error occurred
      throw error;
    }
  }

  static async registerUser(email, displayName = null) {
    try {
      // Check if user already exists
      const existingCustomer = await this.findCustomerByEmail(email);
      if (existingCustomer) {
        return existingCustomer;
      }

      const response = await fetch(`${this.baseUrl}/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          name: displayName || email, // Use display name or full email
          email: email 
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const newCustomer = await response.json();

      // Ensure we have walletIndex for compatibility
      if (newCustomer) {
        if (newCustomer.wallet_index !== undefined && newCustomer.walletIndex === undefined) {
          newCustomer.walletIndex = newCustomer.wallet_index;
        }
        if (newCustomer.index !== undefined && newCustomer.walletIndex === undefined) {
          newCustomer.walletIndex = newCustomer.index;
        }
      }
      return newCustomer;
    } catch (error) {
      throw error;
    }
  }

  static async getUserWalletStatus(email) {
    try {

      // Find customer by email first
      let customer = await this.findCustomerByEmail(email);

      if (!customer) {

        // Try to register the user using admin endpoint
        try {
          const registered = await this.registerUserAdmin(email);
          if (registered) {
            customer = registered;
          } else {
            throw new Error('User not found and registration failed');
          }
        } catch (adminError) {
          throw new Error('User not found and registration failed');
        }
      } else {
      }

      // Get customer details - use index if available, otherwise walletIndex
      const customerIndex = customer.index !== undefined ? customer.index : customer.walletIndex;

      const url = `${WALLETS_API_BASE_URL}/customers/${customerIndex}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Format response to match expected structure
      // API returns both formats - check top level first, then balance object
      const tronUsdt = parseFloat(data.usdt_tron || data.balance?.usdt_tron || data.tronUsdtBalance || 0);
      const ethUsdt = parseFloat(data.usdt_eth || data.balance?.usdt_eth || data.ethUsdtBalance || 0);
      const totalUsdt = parseFloat(data.total_usdt || (tronUsdt + ethUsdt));

      return {
        email: email,
        walletAddress: data.tron_address || data.tronAddress,
        balance: {
          usdt: totalUsdt,
          trx: parseFloat(data.tron_balance || data.tronBalance || data.balance?.trx || 0),
          eth: parseFloat(data.eth_balance || data.ethBalance || data.balance?.eth || 0),
          usdt_tron: tronUsdt,
          usdt_eth: ethUsdt
        },
        balances: {
          available: totalUsdt,
          unconfirmed: 0,
          totalDeposits: 0,
          totalTopUp: 0,
          stackOnChain: totalUsdt,
          totalWithdrawals: 0
        },
        receiveAddresses: [
          {
            chainId: '1',
            address: {
              address: data.eth_address || data.ethAddress,
              link: `https://etherscan.io/address/${data.eth_address || data.ethAddress}`
            }
          },
          {
            chainId: 'tron',
            address: {
              address: data.tron_address || data.tronAddress,
              link: `https://tronscan.org/#/address/${data.tron_address || data.tronAddress}`
            }
          }
        ],
        topUpRequests: [],
        customerIndex: data.wallet_index || data.index || data.walletIndex,
        name: data.name
      };
    } catch (error) {
      throw error;
    }
  }

  static async createTopUpRequest(email, amount, san, network = 'tron', cardAmount = null, fee = null) {
    try {
      // Find customer by email first
      const customer = await this.findCustomerByEmail(email);
      if (!customer) {
        throw new Error('User not found');
      }

      // Create top-up request - temporarily use direct endpoint for all environments
      const functionUrl = `${WALLETS_API_BASE_URL}/deposits/direct`;
      // const functionUrl = process.env.NODE_ENV === 'production'
      //   ? 'https://us-central1-cardsx-a303a.cloudfunctions.net/processDirectDeposit'
      //   : `${WALLETS_API_BASE_URL}/deposits/direct`;

      const customerIndex = customer.wallet_index !== undefined ? customer.wallet_index : customer.walletIndex;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_index: customerIndex,
          amount: amount,  // Total amount to charge (including fee)
          card_amount: cardAmount || amount,  // Amount to load on card
          fee: fee,  // Fee amount (for tracking)
          san: san,
          currency: 'USDT',
          network: network
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  static async getTopUpRequests(email) {
    try {
      // Find customer by email first
      const customer = await this.findCustomerByEmail(email);
      if (!customer) {
        throw new Error('User not found');
      }

      // Get deposits for this customer
      const customerIndex = customer.walletIndex !== undefined ? customer.walletIndex : customer.index;
      const response = await fetch(`${WALLETS_API_BASE_URL}/deposits/customer/${customerIndex}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Check if deposits is an array
      const deposits = Array.isArray(data) ? data : (data.deposits || []);
      
      // Format as top-up requests
      return deposits.map(dep => ({
        id: dep.timestamp,
        email: email,
        amount: dep.depositInfo?.amountToCard || dep.amount,
        san: dep.depositInfo?.san || dep.san,
        status: dep.depositInfo?.capitalistTxId ? 'completed' : 'pending',
        createdAt: dep.timestamp,
        fee: dep.depositInfo?.fee || dep.fee,
        totalDeducted: dep.depositInfo?.totalDeducted || dep.amount
      }));
    } catch (error) {
      throw error;
    }
  }

  static async getTransactionHistory(email) {
    try {
      // Find customer by email first
      const customer = await this.findCustomerByEmail(email);
      if (!customer) {
        throw new Error('User not found');
      }

      // Get transaction history
      const customerIndex = customer.walletIndex !== undefined ? customer.walletIndex : customer.index;
      const response = await fetch(`${WALLETS_API_BASE_URL}/transactions/customer/${customerIndex}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // If endpoint doesn't exist, return empty array
        if (response.status === 404) {
          return [];
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const transactions = Array.isArray(data) ? data : (data.transactions || []);
      
      return transactions;
    } catch (error) {
      return [];
    }
  }
}

export default WalletsAPI;
